import {
  Box,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Field,
  Text,
} from '@optiaxiom/react';
import { IconPlus, IconTrashCan } from '@optimizely/axiom-icons';

import useProductScopeSettings from '../hooks/useProductScopeSettings';
import { UserInstruction } from '../types';

export default function UserInstructionProductScope({
  userInstruction,
  onUpdate,
}: {
  userInstruction: UserInstruction;
  onUpdate: (userInstruction: UserInstruction) => void;
}) {
  const {
    products,
    productsToRender,
    productInstancesToRender,
    selectableProductNames,
    selectableProductInstances,
    addProductScope,
    setAddProductScope,
    newProductInstance,
    setNewProductInstance,
    handleAddProductInstance,
    handleRemoveProductInstance,
    handleAddAllProductInstances,
    handleRemoveProduct,
  } = useProductScopeSettings({ userInstruction, onUpdate });

  const showAddProductInstanceButton =
    !addProductScope && productsToRender.length === 0 && productInstancesToRender.length === 0;

  if (Object.keys(products).length === 0) {
    return null;
  }

  return (
    <Box gap="8" w="full">
      {!showAddProductInstanceButton ? (
        <Box display="flex" gap="12" w="full">
          <Field label="Product" required w="1/2">
            {productsToRender.map(product => (
              <DropdownMenu key={`${product.product_name}-${product.product_sku}`}>
                <DropdownMenuTrigger mb="8">
                  <Text>{product.product_name}</Text>
                </DropdownMenuTrigger>
              </DropdownMenu>
            ))}
            {productInstancesToRender.map(productInstance => (
              <DropdownMenu key={`${productInstance.product_id}-${productInstance.id}`}>
                <DropdownMenuTrigger mb="8">
                  <Text>{productInstance.product_name}</Text>
                </DropdownMenuTrigger>
              </DropdownMenu>
            ))}
            {addProductScope && (
              <DropdownMenu>
                <DropdownMenuTrigger>
                  <Box>
                    <Text>{newProductInstance?.product_name || 'Select Product'}</Text>
                  </Box>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  {selectableProductNames.map(product_name => (
                    <DropdownMenuItem
                      key={product_name}
                      onSelect={() => setNewProductInstance({ product_name })}
                    >
                      {product_name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </Field>

          <Field label="Instance" required w="1/2">
            {productsToRender.map(product => (
              <DropdownMenu key={`${product.product_name}-${product.product_sku}-all`}>
                <DropdownMenuTrigger mb="8">
                  <Text>All</Text>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  {products[product.product_name].instances.map(instance => (
                    <DropdownMenuItem
                      key={instance.id}
                      onSelect={() => handleAddProductInstance(instance)}
                      disabled={instance.disabled}
                    >
                      {instance.nickname ?? instance.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            ))}
            {productInstancesToRender.map(productInstance => (
              <DropdownMenu key={`${productInstance.id}-${productInstance.product_name}-instance`}>
                <DropdownMenuTrigger mb="8">
                  <Text>{productInstance.nickname ?? productInstance.name}</Text>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem
                    onSelect={() => handleAddAllProductInstances(productInstance.product_name)}
                  >
                    All
                  </DropdownMenuItem>
                  {selectableProductInstances.map(instance => (
                    <DropdownMenuItem
                      key={instance.id}
                      onSelect={() => handleAddProductInstance(instance)}
                      disabled={instance.disabled}
                    >
                      {instance.nickname ?? instance.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            ))}
            {addProductScope && (
              <DropdownMenu>
                <DropdownMenuTrigger>
                  <Box>
                    <Text>{newProductInstance?.instance?.name || 'Select Instance'}</Text>
                  </Box>
                </DropdownMenuTrigger>
                {newProductInstance?.product_name && (
                  <DropdownMenuContent>
                    <DropdownMenuItem
                      onSelect={() => handleAddAllProductInstances(newProductInstance.product_name)}
                    >
                      All
                    </DropdownMenuItem>
                    {selectableProductInstances.map(instance => (
                      <DropdownMenuItem
                        key={instance.id}
                        onSelect={() => handleAddProductInstance(instance)}
                        disabled={instance.disabled}
                      >
                        {instance.nickname ?? instance.name}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                )}
              </DropdownMenu>
            )}
          </Field>

          <Box pt="24" display="flex" flexDirection="column" gap="12">
            {productsToRender.map((product, index) => (
              <Box
                // eslint-disable-next-line react/no-array-index-key
                key={`${product.product_name}-${product.product_sku}-${index}`}
                display="flex"
                alignItems="center"
              >
                <Button
                  appearance="subtle"
                  aria-label="Remove Product"
                  icon={<IconTrashCan />}
                  onClick={() => handleRemoveProduct(product.product_sku)}
                />
                {productInstancesToRender.length === 0 &&
                  index === productsToRender.length - 1 &&
                  selectableProductNames.length > 0 && (
                    <Button
                      appearance="subtle"
                      aria-label="Add Product Instance"
                      icon={<IconPlus />}
                      onClick={() => setAddProductScope(true)}
                    />
                  )}
              </Box>
            ))}
            {productInstancesToRender.map((productInstance, index) => (
              <Box
                // eslint-disable-next-line react/no-array-index-key
                key={`${productInstance.product_id}-${productInstance.id}-${index}`}
                display="flex"
                alignItems="center"
              >
                <Button
                  appearance="subtle"
                  aria-label="Remove Product Instance"
                  icon={<IconTrashCan />}
                  onClick={() => handleRemoveProductInstance(productInstance.id)}
                />
                {index === productInstancesToRender.length - 1 &&
                  selectableProductNames.length > 0 && (
                    <Button
                      appearance="subtle"
                      aria-label="Add Product Instance"
                      icon={<IconPlus />}
                      onClick={() => setAddProductScope(true)}
                    />
                  )}
              </Box>
            ))}
          </Box>
        </Box>
      ) : (
        <Button icon={<IconPlus />} iconPosition="start" onClick={() => setAddProductScope(true)}>
          Add Product
        </Button>
      )}
    </Box>
  );
}
